
import { useEffect, useState } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom'

import { useAuthStore } from '../stores/authStore'
import api from '../lib/axios'

import DashboardLayout from '../components/layout/DashboardLayout'
import PublicLayout from '../components/layout/PublicLayout'

// PUBLIC
import Home from '../pages/public/Home'
import Login from '../pages/public/Login'
import Register from '../pages/public/Register'
import SlugRouter from '../pages/public/SlugRouter'
import MenuPagePublic from '../pages/public/MenuPagePublic'
import GoogleReviewPagePublic from '../pages/public/GoogleReviewPagePublic'
import Products from '../pages/public/Products'
import Cart from '../pages/public/Cart'
import CartConfirmation from '../pages/public/CartConfirmation'

// DASHBOARD
import Overview from '../pages/dashboard/Overview'
import MyPages from '../pages/dashboard/MyPages'
import GeneratorQR from '../pages/dashboard/GeneratorQR'
import Analytics from '../pages/dashboard/Analytics'
import Leads from '../pages/dashboard/Leads'
import Subscription from '../pages/dashboard/Subscription'
import PageEditor from '../pages/dashboard/PageEditor'
import MenuPageEditor from '../pages/dashboard/MenuPageEditor'
import OrdersDashboard from '../pages/dashboard/Ordersdashboard'
import MenuOrdersEntry from '../pages/dashboard/MenuOrdersEntry'
import GoogleReviewPageEditor from '../pages/dashboard/GoogleReviewPageEditor'
import Orders from '../pages/dashboard/Orders'
import Profile from '../pages/dashboard/Profile'
import Settings from '../pages/dashboard/Settings'
import Support from '../pages/dashboard/Support'

// ADMIN
import AdminProducts from '../pages/dashboard/AdminProducts'
import AdminOrders from '../pages/dashboard/AdminOrders'
import AdminSubscriptions from '../pages/dashboard/AdminSubscriptions'
import AdminStatistics from '../pages/admin/AdminStatistics'
import AdminNfcTools from '../pages/dashboard/AdminNfcTools'

import AdminRoute from './AdminRoute'

// EXTRAIRE LES PAGES QUEL QUE SOIT LE FORMAT API
function extractPages(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.data?.data)) return data.data.data
  if (Array.isArray(data?.pages)) return data.pages

  return []
}

// DÉTECTER LE MENU NFC
function userHasMenu(pages) {
  return pages.some((page) => {
    const type = String(
      page?.type || page?.page_type || ''
    ).toLowerCase().trim()

    return type === 'menu'
  })
}

// AUTH BOOTSTRAP
function useAuthBootstrap() {
  const fetchMe = useAuthStore((state) => state.fetchMe)

  useEffect(() => {
    fetchMe()
  }, [fetchMe])
}

// AUTH GATE
function AppGate() {
  const isInitialized = useAuthStore(
    (state) => state.isInitialized
  )

  const user = useAuthStore((state) => state.user)
  const location = useLocation()

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9ff]">
        <div className="w-8 h-8 border-2 border-[#006b5f]/30 border-t-[#006b5f] rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    )
  }

  return <Outlet />
}

// CLIENT ROUTE
function ClientRoute({ allowMenu = false }) {
  const isAdmin = useAuthStore((state) => state.isAdmin())

  const [checking, setChecking] = useState(
    allowMenu && !isAdmin
  )

  const [hasMenu, setHasMenu] = useState(false)

  useEffect(() => {
    let active = true

    const checkMenu = async () => {
      if (!allowMenu || isAdmin) {
        setChecking(false)
        setHasMenu(true)
        return
      }

      setChecking(true)

      try {
        const { data } = await api.get('/pages')
        const pages = extractPages(data)
        const found = userHasMenu(pages)

        console.log('Pages récupérées:', pages)
        console.log('Menu NFC trouvé:', found)

        if (active) {
          setHasMenu(found)
        }
      } catch (error) {
        console.error(
          'Erreur vérification Menu NFC:',
          error
        )

        if (active) {
          setHasMenu(false)
        }
      } finally {
        if (active) {
          setChecking(false)
        }
      }
    }

    checkMenu()

    return () => {
      active = false
    }
  }, [allowMenu, isAdmin])

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
      </div>
    )
  }

  if (allowMenu && !isAdmin && !hasMenu) {
    return <Navigate to="/app/pages" replace />
  }

  return <Outlet />
}

// DEFAULT DASHBOARD PAGE
function DashboardIndex() {
  const isAdmin = useAuthStore((state) => state.isAdmin())

  return (
    <Navigate
      to={isAdmin ? '/app/overview' : '/app/pages'}
      replace
    />
  )
}

// NOT FOUND
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-graysoft">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-navy mb-4">
          404
        </h1>

        <p className="text-gray-500 mb-6">
          Page introuvable
        </p>

        <a
          href="/"
          className="px-6 py-3 bg-teal text-white rounded-xl font-semibold"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  )
}

// ROUTER
export default function AppRouter() {
  useAuthBootstrap()

  return (
    <BrowserRouter>
      <Routes>
        {/* PUBLIC */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/produits" element={<Products />} />
          <Route path="/panier" element={<Cart />} />

          <Route
            path="/panier/confirmation/:groupRef"
            element={<CartConfirmation />}
          />
        </Route>

        {/* AUTHENTICATED DASHBOARD */}
        <Route path="/app" element={<AppGate />}>
          <Route element={<DashboardLayout />}>
            <Route index element={<DashboardIndex />} />

            {/* CLIENT + ADMIN */}
            <Route element={<ClientRoute />}>
              <Route path="pages" element={<MyPages />} />

              {/* ÉDITION DES PAGES EXISTANTES */}
              <Route
                path="pages/:id/edit"
                element={<PageEditor />}
              />

              <Route
                path="menus/:id/edit"
                element={<MenuPageEditor />}
              />

              <Route
                path="reviews/:id/edit"
                element={<GoogleReviewPageEditor />}
              />

              {/* COMPTE */}
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
            </Route>

            {/* COMMANDES MENU NFC */}
            <Route element={<ClientRoute allowMenu />}>
              <Route
                path="menu-orders"
                element={<MenuOrdersEntry />}
              />

              <Route
                path="menus/:id/orders"
                element={<OrdersDashboard />}
              />
            </Route>

            {/* ADMIN ONLY */}
            <Route path="admin" element={<AdminRoute />}>
              <Route
                index
                element={
                  <Navigate
                    to="/app/admin/statistics"
                    replace
                  />
                }
              />

              <Route
                path="statistics"
                element={<AdminStatistics />}
              />

              <Route
                path="products"
                element={<AdminProducts />}
              />

              <Route
                path="orders"
                element={<AdminOrders />}
              />

              <Route
                path="subscriptions"
                element={<AdminSubscriptions />}
              />

              <Route
                path="nfc-tools"
                element={<AdminNfcTools />}
              />
            </Route>

            {/* AUTRES FONCTIONS ADMIN UNIQUEMENT */}
            <Route element={<AdminRoute />}>
              <Route path="overview" element={<Overview />} />

              {/* CRÉATION DES PAGES */}
              <Route
                path="pages/new"
                element={<PageEditor />}
              />

              <Route
                path="menus/new"
                element={<MenuPageEditor />}
              />

              <Route
                path="reviews/new"
                element={<GoogleReviewPageEditor />}
              />

              {/* OUTILS */}
              <Route path="qrs" element={<GeneratorQR />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="leads" element={<Leads />} />

              {/* COMMANDES CLIENTS */}
              <Route path="orders" element={<Orders />} />

              {/* ABONNEMENT */}
              <Route
                path="subscription"
                element={<Subscription />}
              />
            </Route>
          </Route>
        </Route>

        {/* PUBLIC DEDICATED PAGES */}
        <Route
          path="/menu/:slug"
          element={<MenuPagePublic />}
        />

        <Route
          path="/avis/:slug"
          element={<GoogleReviewPagePublic />}
        />

        {/* PUBLIC SLUG */}
        <Route
          path="/:slug"
          element={<SlugRouter />}
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}
